import { useEffect } from 'react';

import type { Campaign, OmniCampaign, TOmniIntegration } from '@feathr/blackbox';
import { EIntegrationTypes } from '@feathr/blackbox';

interface IUseSyncChannelsWithCreatives {
  campaign: Campaign;
  /** Whether any unarchived Display creatives exist on the campaign */
  hasDisplayCreatives: boolean;
  /** Whether any unarchived Meta creatives exist on the campaign */
  hasMetaCreatives: boolean;
  /** True while the campaign's creatives are still loading */
  isPending: boolean;
}

/**
 * Keeps an Omni campaign's selected channels in step with its creatives.
 *
 * A channel that has unarchived creatives is added to `integrations` if missing, so the
 * channel card is never locked in an unselected state. Published and stopped campaigns and
 * non-Omni campaigns are left untouched.
 */
export function useSyncChannelsWithCreatives({
  campaign,
  hasDisplayCreatives,
  hasMetaCreatives,
  isPending,
}: IUseSyncChannelsWithCreatives): void {
  useEffect(() => {
    if (isPending || !campaign.isOmniCampaign || campaign.isDisabledCampaignState) {
      return;
    }
    const omni = campaign as OmniCampaign;
    const integrations: TOmniIntegration[] = omni.get('integrations') ?? [];
    const missing: TOmniIntegration[] = [];
    if (hasDisplayCreatives && !integrations.includes(EIntegrationTypes.Tradedesk)) {
      missing.push(EIntegrationTypes.Tradedesk);
    }
    if (hasMetaCreatives && !integrations.includes(EIntegrationTypes.Meta)) {
      missing.push(EIntegrationTypes.Meta);
    }
    if (!missing.length) {
      return;
    }
    omni.set({ integrations: [...integrations, ...missing] });
  }, [campaign, hasDisplayCreatives, hasMetaCreatives, isPending]);
}
